import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApi } from '@/lib/api-context';

import {
  onlineCaptureResponseSchema,
  onlineEditResponseSchema,
  onlineTakeLmsResponseSchema,
  onlineViewResponseSchema,
  type OnlineCapture,
  type OnlineEdit,
  type OnlineTakeLms,
  type OnlineView,
} from './types';

// Edición en línea (Opción A): el editor de Office NO se incrusta (CSP frame-ancestors).
// La vista es de solo lectura (iframe embedUrl); la edición se abre en pop-out (editUrl)
// y al volver se captura la versión. El servidor es la única autoridad sobre el binario.

const DOCS = '/api/v1/quality/documents';

/** Vuelve a pedir la vista del documento (en todas las empresas desde las que se abrió). */
function invalidateView(qc: QueryClient, entryId: string): Promise<void> {
  return qc.invalidateQueries({
    predicate: (q) => q.queryKey[0] === 'online-view' && q.queryKey[2] === entryId,
  });
}

interface Options {
  companyId: string | null;
  enabled: boolean;
}

export function useOnlineView(
  id: string,
  { companyId, enabled }: Options,
): UseQueryResult<OnlineView> {
  const api = useApi();
  return useQuery({
    queryKey: ['online-view', companyId, id],
    queryFn: async () => {
      const raw = await api.get<unknown>(`${DOCS}/${id}/online/view`);
      return onlineViewResponseSchema.parse(raw).data;
    },
    enabled,
    // La vista firma una URL efímera; no reintentar en cascada ni servir cache viejo.
    staleTime: 0,
  });
}

export function useOnlineEdit(): UseMutationResult<OnlineEdit, Error, { entryId: string }> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ entryId }) => {
      const raw = await api.post<unknown>(`${DOCS}/${entryId}/online/edit`, {});
      return onlineEditResponseSchema.parse(raw).data;
    },
    // La primera edición siembra el archivo: la vista pasa a mostrar el borrador real. Sin
    // esperar la recarga: la pestaña de Office debe abrirse pegada al clic, o el navegador
    // la bloquea.
    onSuccess: (_data, { entryId }) => {
      void invalidateView(qc, entryId);
    },
  });
}

export function useOnlineCapture(): UseMutationResult<OnlineCapture, Error, { entryId: string }> {
  const api = useApi();
  return useMutation({
    mutationFn: async ({ entryId }) => {
      const raw = await api.post<unknown>(`${DOCS}/${entryId}/online/capture`, {});
      return onlineCaptureResponseSchema.parse(raw).data;
    },
  });
}

/** Calidad toma la versión cargada en el gestor documental fuera del QMS como borrador. */
export function useTakeLmsVersion(): UseMutationResult<OnlineTakeLms, Error, { entryId: string }> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ entryId }) => {
      const raw = await api.post<unknown>(`${DOCS}/${entryId}/online/take-lms-version`, {});
      return onlineTakeLmsResponseSchema.parse(raw).data;
    },
    onSuccess: (_data, { entryId }) => invalidateView(qc, entryId),
  });
}
